import { formatNumberBasedOnUser } from "utils/namesManipulation";
import { getCSSVar } from "utils/styling";

import type { EnergyAnalysisRow } from "../model/types";

import { collectYears, sumValues } from "./aggregate";
import { buildCategoryColorMap } from "./colors";
import { toEnergyAnalysisLabel } from "./labels";

const pairKey = (row: EnergyAnalysisRow) => `${row.useType}::${row.utility}`;
const pairLabel = (row: Pick<EnergyAnalysisRow, "useType" | "utility">) =>
  `${toEnergyAnalysisLabel(row.useType)} · ${toEnergyAnalysisLabel(row.utility)}`;
const formatChartNumber = (value: number) => formatNumberBasedOnUser(value, 2);
const commonGrid = { bottom: 58, containLabel: true, left: 16, right: 24, top: 20 };

const collectPairs = (rows: EnergyAnalysisRow[]) => {
  const pairs = new Map<string, Pick<EnergyAnalysisRow, "useType" | "utility">>();
  rows.forEach((row) => pairs.set(pairKey(row), row));
  return [...pairs.values()];
};

const selectMilestoneYears = (years: number[], count = 5) => {
  if (years.length <= count) return years;
  return [
    ...new Set(
      Array.from({ length: count }, (_, index) =>
        years[Math.round((index * (years.length - 1)) / (count - 1))]
      )
    ),
  ].filter((year): year is number => year !== undefined);
};

const valueFor = (rows: EnergyAnalysisRow[], year: number, useType: string, utility: string) =>
  sumValues(
    rows.filter((row) => row.year === year && row.useType === useType && row.utility === utility)
  );

export const buildCombinationTrendHeatmapOption = (rows: EnergyAnalysisRow[], unit: string) => {
  const years = selectMilestoneYears(collectYears(rows));
  const pairs = collectPairs(rows)
    .map((pair) => ({
      ...pair,
      magnitude: sumValues(
        rows.filter((row) => row.useType === pair.useType && row.utility === pair.utility)
      ),
    }))
    .sort((left, right) => right.magnitude - left.magnitude)
    .slice(0, 10);
  const values = pairs.flatMap((pair, yIndex) =>
    years.map((year, xIndex) => [
      xIndex,
      yIndex,
      valueFor(rows, year, pair.useType, pair.utility),
    ])
  );
  const max = values.reduce((result, point) => Math.max(result, Number(point[2]) || 0), 0);
  return {
    dataZoom: [],
    grid: { ...commonGrid, bottom: 42, left: 12, right: 82 },
    series: [
      {
        data: values,
        label: {
          formatter: ({ value }: { value: number[] }) => formatChartNumber(value[2] ?? 0),
          show: true,
        },
        type: "heatmap",
      },
    ],
    tooltip: {
      formatter: ({ value }: { value: number[] }) => {
        const pair = pairs[value[1] ?? 0];
        const year = years[value[0] ?? 0];
        return `${pair ? pairLabel(pair) : ""}, ${year}: ${formatChartNumber(value[2] ?? 0)} ${unit}`;
      },
      trigger: "item",
    },
    visualMap: {
      calculable: true,
      inRange: {
        color: [getCSSVar("--core-color-star-100"), getCSSVar("--core-color-mystic-red-600")],
      },
      max,
      min: 0,
      orient: "vertical",
      right: 0,
      top: "middle",
    },
    xAxis: [{ boundaryGap: true, data: years, type: "category" }],
    yAxis: [{ boundaryGap: true, data: pairs.map(pairLabel), type: "category" }],
  };
};

export const buildCombinationChangeBarOption = (rows: EnergyAnalysisRow[], unit: string) => {
  const years = collectYears(rows);
  const startYear = years[0];
  const endYear = years[years.length - 1];
  if (startYear === undefined || endYear === undefined) return {};
  const changes = collectPairs(rows)
    .map((pair) => ({
      label: pairLabel(pair),
      value:
        valueFor(rows, endYear, pair.useType, pair.utility) -
        valueFor(rows, startYear, pair.useType, pair.utility),
    }))
    .filter((item) => Math.abs(item.value) > 0.0001)
    .sort((left, right) => Math.abs(right.value) - Math.abs(left.value))
    .slice(0, 10)
    .reverse();
  return {
    dataZoom: [],
    grid: { right: 96 },
    series: [
      {
        data: changes.map((item) => ({
          itemStyle: { color: item.value <= 0 ? "#4f9a6d" : "#bd5c43" },
          value: item.value,
        })),
        label: {
          formatter: ({ value }: { value: number }) =>
            `${value > 0 ? "+" : ""}${formatChartNumber(value)}`,
          position: "right",
          show: true,
        },
        type: "bar",
      },
    ],
    tooltip: {
      valueFormatter: (value: number) => `${formatChartNumber(value)} ${unit}`,
      trigger: "axis",
    },
    xAxis: [{ axisLabel: { formatter: formatChartNumber }, type: "value" }],
    yAxis: [{ boundaryGap: true, data: changes.map((item) => item.label), type: "category" }],
  };
};

export const buildMixChangeMatrixOption = (rows: EnergyAnalysisRow[], unit: string) => {
  const years = collectYears(rows);
  const startYear = years[0];
  const endYear = years[years.length - 1];
  if (startYear === undefined || endYear === undefined) return {};
  const useTypes = [...new Set(rows.map((row) => row.useType))].sort();
  const utilities = [...new Set(rows.map((row) => row.utility))].sort();
  const values = useTypes.flatMap((useType, yIndex) =>
    utilities.map((utility, xIndex) => {
      const start = valueFor(rows, startYear, useType, utility);
      const end = valueFor(rows, endYear, useType, utility);
      const hasValue = start > 0 || end > 0;
      const change = hasValue ? end - start : null;
      const percentage = start > 0 && change !== null ? (change / start) * 100 : null;
      return [xIndex, yIndex, change, percentage];
    })
  );
  const extent = values.reduce(
    (result, point) => Math.max(result, Math.abs(Number(point[2]) || 0)),
    0
  );
  return {
    dataZoom: [],
    grid: commonGrid,
    series: [
      {
        data: values,
        itemStyle: { borderColor: "#ffffff", borderWidth: 2 },
        label: {
          formatter: ({ value }: { value: (number | null)[] }) => {
            const change = value[2];
            const percentage = value[3];
            if (change === null) return "–";
            const percentLabel =
              percentage === null ? "" : `\n(${formatChartNumber(percentage)}%)`;
            return `${formatChartNumber(change)}${percentLabel}`;
          },
          show: true,
        },
        type: "heatmap",
      },
    ],
    tooltip: {
      formatter: ({ value }: { value: (number | null)[] }) => {
        const utility = utilities[Number(value[0])];
        const useType = useTypes[Number(value[1])];
        const change = value[2];
        return `${toEnergyAnalysisLabel(useType ?? "")} · ${toEnergyAnalysisLabel(utility ?? "")}: ${
          change === null ? "No data" : `${formatChartNumber(change)} ${unit}`
        }`;
      },
      trigger: "item",
    },
    visualMap: {
      dimension: 2,
      inRange: { color: ["#4f8067", "#eef0ed", "#bd5c43"] },
      max: extent || 1,
      min: -(extent || 1),
      show: false,
    },
    xAxis: [{ boundaryGap: true, data: utilities.map(toEnergyAnalysisLabel), type: "category" }],
    yAxis: [{ boundaryGap: true, data: useTypes.map(toEnergyAnalysisLabel), type: "category" }],
  };
};

export const buildMixMosaicOption = (
  rows: EnergyAnalysisRow[],
  unit: string,
  snapshotYear?: number
) => {
  const years = collectYears(rows);
  const year = snapshotYear !== undefined && years.includes(snapshotYear) ? snapshotYear : years[years.length - 1];
  const yearRows = rows.filter((row) => row.year === year);
  const total = sumValues(yearRows);
  const useTypes = [...new Set(rows.map((row) => row.useType))]
    .map((useType) => ({
      key: useType,
      value: sumValues(rows.filter((row) => row.useType === useType)),
    }))
    .sort((left, right) => right.value - left.value)
    .map((item) => item.key);
  const utilities = [...new Set(rows.map((row) => row.utility))].sort();
  const useTypeColors = buildCategoryColorMap(useTypes);
  const utilityColors = buildCategoryColorMap(utilities);
  const snapshotValues = new Map<string, number>();
  yearRows.forEach((row) => {
    const key = pairKey(row);
    snapshotValues.set(key, (snapshotValues.get(key) ?? 0) + row.value);
  });
  const data = useTypes.map((useType) => {
    const children = utilities
      .filter((utility) =>
        rows.some((row) => row.useType === useType && row.utility === utility && row.value > 0)
      )
      .map((utility) => ({
        itemStyle: { color: utilityColors[utility] },
        name: toEnergyAnalysisLabel(utility),
        value: snapshotValues.get(`${useType}::${utility}`) ?? 0,
      }));
    return {
      children,
      itemStyle: { color: useTypeColors[useType] },
      name: toEnergyAnalysisLabel(useType),
      value: children.reduce((result, child) => result + child.value, 0),
    };
  });
  return {
    animation: false,
    dataZoom: [],
    series: [
      {
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
        bottom: 16,
        breadcrumb: { show: false },
        data,
        id: "mix-mosaic",
        label: {
          formatter: ({ name, value }: { name: string; value: number }) => {
            const share = total > 0 ? (value / total) * 100 : 0;
            return `${name}\n${formatChartNumber(share)}%`;
          },
          overflow: "truncate",
          show: true,
        },
        levels: [
          {
            itemStyle: {
              borderColor: "#ffffff",
              borderRadius: 10,
              borderWidth: 6,
              gapWidth: 6,
            },
            upperLabel: { height: 26, show: true },
          },
          {
            itemStyle: {
              borderColor: "#ffffff",
              borderRadius: 7,
              borderWidth: 4,
              gapWidth: 4,
            },
          },
        ],
        left: 16,
        roam: false,
        right: 16,
        sort: false,
        top: 16,
        type: "treemap",
        universalTransition: false,
        upperLabel: { height: 26, show: true },
      },
    ],
    tooltip: {
      valueFormatter: (value: number) => `${formatChartNumber(value)} ${unit}`,
      trigger: "item",
    },
  };
};
